import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { formatCurrency } from '../charts/d3/format';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import {
  INTEREST_COLOR,
  PRINCIPAL_COLOR,
} from '../../utils/investmentCompounding';
import type { PrincipalInterestSplit } from '../../utils/investmentCompounding';

type CategoryPrincipalInterestPanelProps = {
  categories: PrincipalInterestSplit[];
  years: number;
};

/**
 * End-of-horizon composition by path.
 * Bars are always full-width (share of ending balance), not scaled to the largest path —
 * so blue/green read as “what % is yours vs earned.” Totals + interest multiple carry size.
 */
export function CategoryPrincipalInterestPanel({
  categories,
  years,
}: CategoryPrincipalInterestPanelProps) {
  if (categories.length === 0) return null;

  const maxTotal = Math.max(...categories.map((c) => c.total), 1);

  return (
    <View style={styles.wrap}>
      <Text style={styles.title}>How each path splits</Text>
      <Text style={styles.hint}>
        After {years} years — every bar is 100% of that path’s ending balance. Blue is money you put
        in; green is interest. Same contributions → same principal; rate only changes interest.
      </Text>

      <View style={styles.colorKey}>
        <View style={styles.keyItem}>
          <View style={[styles.keyDot, { backgroundColor: PRINCIPAL_COLOR }]} />
          <Text style={styles.keyText}>Your money</Text>
        </View>
        <View style={styles.keyItem}>
          <View style={[styles.keyDot, { backgroundColor: INTEREST_COLOR }]} />
          <Text style={styles.keyText}>Interest earned</Text>
        </View>
      </View>

      {categories.map((cat) => {
        const principalShare = cat.total > 0 ? cat.principal / cat.total : 0;
        const interestShare = cat.total > 0 ? cat.interest / cat.total : 0;
        const principalPct = Math.round(principalShare * 100);
        const interestPct = cat.interestSharePct;
        const interestMultiple =
          cat.principal > 0 ? cat.interest / cat.principal : 0;
        const vsTopPct = Math.round((cat.total / maxTotal) * 100);
        const isMix = cat.id === 'mix';
        const isTop = cat.total === maxTotal && cat.total > 0;

        return (
          <View
            key={cat.id}
            style={[styles.card, isMix && styles.cardHighlight]}
          >
            <View style={styles.rowHeader}>
              <View style={styles.rowTitleBlock}>
                <Text style={styles.rowLabel} numberOfLines={1}>
                  {cat.label}
                </Text>
                <Text style={styles.rowMeta}>
                  {cat.ratePct.toFixed(1)}% return
                  {isTop ? ' · largest' : vsTopPct < 100 ? ` · ${vsTopPct}% of largest` : ''}
                </Text>
              </View>
              <View style={styles.totalBlock}>
                <Text style={[styles.rowTotal, isTop && styles.rowTotalAccent]}>
                  {formatCurrency(cat.total)}
                </Text>
                {interestMultiple > 0 ? (
                  <Text style={styles.multiple}>
                    Interest {interestMultiple.toFixed(1)}× principal
                  </Text>
                ) : null}
              </View>
            </View>

            {/* Full-width composition — always sums to 100% */}
            <View style={styles.bar}>
              <View
                style={[
                  styles.segPrincipal,
                  { flex: Math.max(principalShare, 0.001) },
                ]}
              />
              <View
                style={[
                  styles.segInterest,
                  { flex: Math.max(interestShare, 0.001) },
                ]}
              />
            </View>

            <View style={styles.splitRow}>
              <View style={styles.splitCol}>
                <View style={styles.splitLabelRow}>
                  <View style={[styles.splitTick, { backgroundColor: PRINCIPAL_COLOR }]} />
                  <Text style={styles.splitLabel}>Principal</Text>
                </View>
                <Text style={[styles.splitValue, { color: PRINCIPAL_COLOR }]}>
                  {formatCurrency(cat.principal)}
                </Text>
                <Text style={styles.splitPct}>{principalPct}% of total</Text>
              </View>
              <View style={[styles.splitCol, styles.splitColEnd]}>
                <View style={[styles.splitLabelRow, styles.splitLabelRowEnd]}>
                  <View style={[styles.splitTick, { backgroundColor: INTEREST_COLOR }]} />
                  <Text style={styles.splitLabel}>Interest</Text>
                </View>
                <Text style={[styles.splitValue, styles.splitValueEnd, { color: INTEREST_COLOR }]}>
                  {formatCurrency(cat.interest)}
                </Text>
                <Text style={[styles.splitPct, styles.splitPctEnd]}>
                  {interestPct}% of total
                </Text>
              </View>
            </View>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  title: {
    ...typography.caption,
    color: colors.text,
    fontWeight: '600',
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  colorKey: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
    marginBottom: spacing.xs,
  },
  keyItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  keyDot: {
    width: 8,
    height: 8,
    borderRadius: 2,
  },
  keyText: {
    ...typography.caption,
    color: colors.textMuted,
  },
  card: {
    gap: spacing.sm,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.sm,
    borderRadius: radii.md,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  cardHighlight: {
    borderColor: 'rgba(61, 214, 164, 0.35)',
    backgroundColor: 'rgba(61, 214, 164, 0.06)',
  },
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  rowTitleBlock: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  rowLabel: {
    ...typography.body,
    color: colors.text,
    fontSize: 15,
    fontWeight: '600',
  },
  rowMeta: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 12,
  },
  totalBlock: {
    alignItems: 'flex-end',
    gap: 2,
  },
  rowTotal: {
    ...typography.body,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
    fontSize: 15,
  },
  rowTotalAccent: {
    color: colors.accent,
  },
  multiple: {
    ...typography.caption,
    color: INTEREST_COLOR,
    fontSize: 11,
    fontWeight: '600',
  },
  bar: {
    height: 18,
    borderRadius: radii.sm,
    overflow: 'hidden',
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  segPrincipal: {
    height: '100%',
    backgroundColor: PRINCIPAL_COLOR,
    minWidth: 2,
  },
  segInterest: {
    height: '100%',
    backgroundColor: INTEREST_COLOR,
    minWidth: 2,
  },
  splitRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  splitCol: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  splitColEnd: {
    alignItems: 'flex-end',
  },
  splitLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  splitLabelRowEnd: {
    justifyContent: 'flex-end',
  },
  splitTick: {
    width: 6,
    height: 6,
    borderRadius: 1,
  },
  splitLabel: {
    ...typography.label,
    color: colors.textMuted,
    letterSpacing: 0.4,
  },
  splitValue: {
    ...typography.body,
    fontSize: 14,
    fontWeight: '600',
    fontVariant: ['tabular-nums'],
  },
  splitValueEnd: {
    textAlign: 'right',
  },
  splitPct: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  splitPctEnd: {
    textAlign: 'right',
  },
});
